// Façade of the Get Paid screens (contributor, moderator, admin). The logic
// lives in hooks/ (one per use) and logic/ (pure, tested); this hook composes
// them and keeps the shape every screen already reads from `ctx`.
import { useEffect, useMemo } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { useConfirm } from "@/hooks/use-confirm";
import { useApp } from "@/hooks/use-app-context";
import { useToast } from "@/hooks/use-toast";
import { canModerateTests } from "@/lib/auth/roles";
import { ContractStatus } from "@/lib/types/contracts";
import { SUPPORT_EMAIL } from "./constants";
import { formatCurrency, getRequestPhone, isMonthClosed, renderPagination } from "./helpers";
import { isAdminOrSuper } from "./hooks/get-paid-context";
import { useModeratorTests } from "./hooks/use-moderator-tests";
import { useUserContracts } from "./hooks/use-user-contracts";
import { usePayoutRequests } from "./hooks/use-payout-requests";
import { useAdminBudget } from "./hooks/use-admin-budget";
import { useContributorEarnings } from "./hooks/use-contributor-earnings";
import { useQualificationTest } from "./hooks/use-qualification-test";
import { useModeratorContractsFlow } from "./hooks/use-moderator-contracts-flow";
import { useTestCorrection } from "./hooks/use-test-correction";
import { useModeratorEarnings } from "./hooks/use-moderator-earnings";
import { usePayoutCandidates } from "./hooks/use-payout-candidates";
import { useContributorPayoutRequest } from "./hooks/use-contributor-payout-request";

export type GetPaidRoleTab = "contributor" | "moderator" | "admin";

export function useGetPaid() {
  const { t, language, user, theme, toggleTheme } = useApp();
  const confirmDialog = useConfirm();
  const { lang } = useParams<{ lang: string }>();
  const navigate = useNavigate();
  const { toast } = useToast();
  const [searchParams, setSearchParams] = useSearchParams();

  const currentLang = lang ?? language;
  const locale = language === "fr" ? "fr-FR" : "en-US";
  const currency = "XOF";
  const tPage = t.getPaidPage;
  const base = { t, user, toast };
  const money = { locale, currency };

  const payoutStatusMeta = {
    PENDING: { label: t.getPaid.statusPending, className: "bg-warning text-warning border-warning" },
    APPROVED: { label: t.getPaid.statusApproved, className: "bg-success text-success border-success" },
    PAID: { label: tPage.paid, className: "bg-info text-info border-info" },
    REJECTED: { label: t.getPaid.statusRejected, className: "bg-danger text-danger border-danger" },
  };
  const monthClosed = useMemo(() => isMonthClosed(), []);

  const moderation = useModeratorTests({ ...base, searchParams });
  const contracts = useUserContracts(user);
  const requests = usePayoutRequests(base);
  const admin = useAdminBudget({ ...base, ...money, language });
  const earnings = useContributorEarnings({
    user,
    tPage,
    pendingPayoutAmount: requests.pendingPayoutAmount,
    loadUserContracts: contracts.loadUserContracts,
  });
  const qualification = useQualificationTest({
    ...base,
    currentLang,
    testEligibility: earnings.testEligibility,
    hasValidationContractInFlight: Boolean(contracts.latestPendingUserContract || contracts.latestAwaitingFinalUserContract),
  });
  const contractsFlow = useModeratorContractsFlow({
    ...base,
    correctedTests: moderation.correctedTests,
    validatedTests: moderation.validatedTests,
    setCorrectedTests: moderation.setCorrectedTests,
    activeModTab: moderation.activeModTab,
    activeCorrectedContractTab: moderation.activeCorrectedContractTab,
    loadCorrectedTests: moderation.loadCorrectedTests,
    loadValidatedTests: moderation.loadValidatedTests,
  });
  const correction = useTestCorrection({
    t,
    toast,
    setSearchParams,
    pendingTests: moderation.pendingTests,
    setPendingTests: moderation.setPendingTests,
    setCorrectedTests: moderation.setCorrectedTests,
    setActiveModTab: moderation.setActiveModTab,
  });
  const moderatorEarnings = useModeratorEarnings({
    ...base,
    monthClosed,
    moderatorAmount: admin.payoutSettings.moderatorAmount,
    loadMyPayoutRequests: requests.loadMyPayoutRequests,
  });
  const candidates = usePayoutCandidates({
    ...base,
    ...money,
    confirmDialog,
    monthClosed,
    contributorAmount: admin.payoutSettings.contributorAmount,
    moderatorAmount: admin.payoutSettings.moderatorAmount,
    setLoadingTests: moderation.setLoadingTests,
    loadBudgetAndStats: admin.loadBudgetAndStats,
    moderatorRequestsToProcess: requests.moderatorRequestsToProcess,
    contributorRequestsToProcess: requests.contributorRequestsToProcess,
  });
  const payoutRequest = useContributorPayoutRequest({
    ...base,
    ...money,
    tPage,
    payoutState: earnings.payoutState,
    loadMyPayoutRequests: requests.loadMyPayoutRequests,
  });

  // What each role loads on arrival.
  useEffect(() => {
    if (isAdminOrSuper(user?.role)) {
      admin.loadBudgetAndStats();
      candidates.loadEligibleUsers();
      candidates.loadEligibleModerators();
      requests.loadAdminPayoutRequests();
    } else if (user?.role === "moderator") {
      requests.loadPendingPayoutRequests();
      requests.loadMyPayoutRequests();
      requests.loadAdminPayoutRequests();
    } else if (user?.id) {
      requests.loadMyPayoutRequests();
    }
  }, [user?.role, user?.id]);

  // The moderation tab on screen, plus the contracts behind the corrected tabs.
  useEffect(() => {
    if (canModerateTests(user?.role)) {
      if (moderation.activeModTab === "PENDING") {
        moderation.loadPendingTests();
      } else if (moderation.activeModTab === "VALIDATED") {
        if (moderation.activeCorrectedContractTab === ContractStatus.FINAL_VALIDATED) {
          moderation.loadValidatedTests();
        } else {
          moderation.loadCorrectedTests(moderation.activeCorrectedContractTab);
        }
        contractsFlow.loadModeratorContracts();
      }
    }
  }, [user?.role, moderation.moderationLang, moderation.activeModTab, moderation.activeCorrectedContractTab]);

  const handleViewContributions = () => {
    navigate(`/${currentLang}/my-contributions`);
  };

  const handleOpenContracts = (tab: "pending" | "signed" = "pending") => {
    navigate(`/${currentLang}/user/contracts?tab=${tab}`);
  };

  const handleContactSupport = () => {
    if (typeof window !== "undefined") {
      window.location.href = `mailto:${SUPPORT_EMAIL}`;
    }
  };

  const { testTaking } = qualification;
  const { payoutState } = earnings;

  return {
    user, t, tPage, locale, currency, currentLang, language, navigate,
    theme, toggleTheme, searchParams, setSearchParams,
    monthClosed, loadingUserContracts: contracts.loadingUserContracts, mySubmissions: qualification.mySubmissions,
    currentTest: testTaking.currentTest, selectedTestLang: testTaking.selectedTestLang,
    setSelectedTestLang: testTaking.setSelectedTestLang, selectTestLanguage: qualification.selectTestLanguage,
    dataPhonemes: testTaking.dataPhonemes, dataPhrasesLocalFr: testTaking.dataPhrasesLocalFr, dataPhrasesLocalOnly: testTaking.dataPhrasesLocalOnly,
    testTimeRemaining: testTaking.testTimeRemaining, isTestExpired: testTaking.isTestExpired,
    submittingUserTest: testTaking.submittingUserTest, startingTest: testTaking.startingTest,
    activeItemId: testTaking.activeItemId, timers: testTaking.timers, statuses: testTaking.statuses,
    attempts: testTaking.attempts, audioUrls: testTaking.audioUrls, translations: testTaking.translations,
    startRecording: testTaking.startRecording, stopRecording: testTaking.stopRecording, redoRecording: testTaking.redoRecording,
    handleTranslationFocus: testTaking.handleTranslationFocus, handleTranslationChange: testTaking.handleTranslationChange,
    handleFinalSubmit: testTaking.handleFinalSubmit, handleStartTest: testTaking.handleStartTest,
    allItemsCompleted: testTaking.allItemsCompleted,
    loadingTests: moderation.loadingTests,
    sendingContractTestId: contractsFlow.sendingContractTestId, finalValidatingTestId: contractsFlow.finalValidatingTestId,
    moderationLang: moderation.moderationLang, setModerationLang: moderation.setModerationLang,
    activeModTab: moderation.activeModTab, activeCorrectedContractTab: moderation.activeCorrectedContractTab,
    moderatorContracts: contractsFlow.moderatorContracts,
    finalValidationDialog: contractsFlow.finalValidationDialog, setFinalValidationDialog: contractsFlow.setFinalValidationDialog,
    details: correction.details, itemScores: correction.itemScores, savingScores: correction.savingScores,
    validatedAudios: earnings.validatedAudios, pendingAudios: earnings.pendingAudios, totalContributions: earnings.totalContributions,
    testApproved: earnings.testApproved, validatedAudiosAfterTest: earnings.validatedAudiosAfterTest,
    backendEligibility: earnings.backendEligibility, availableLanguages: qualification.availableLanguages,
    contributedTestLanguages: qualification.contributedTestLanguages, loadingLanguages: qualification.loadingLanguages,
    testEligibility: earnings.testEligibility, testEligibilityByLanguage: qualification.testEligibilityByLanguage,
    selectedLanguageEligibility: qualification.selectedLanguageEligibility,
    testEligibilityDialogOpen: earnings.testEligibilityDialogOpen, budgetInfo: admin.budgetInfo, loadingBudget: admin.loadingBudget,
    paymentStats: admin.paymentStats, adminPayoutRequests: requests.adminPayoutRequests,
    payoutSettings: admin.payoutSettings, setPayoutSettings: admin.setPayoutSettings,
    moderatorNavTab: moderation.moderatorNavTab, moderatorNavItems: moderation.moderatorNavItems,
    visiblePendingTests: moderation.visiblePendingTests, visibleCorrectedTests: moderation.visibleCorrectedTests,
    moderatorPayoutState: moderatorEarnings.moderatorPayoutState, payoutState,
    payoutStatusMeta, correctedContractTabs: moderation.correctedContractTabs,
    adminTab: admin.adminTab, setAdminTab: admin.setAdminTab, adminNavigationItems: admin.adminNavigationItems,
    moderatorRequestsPage: candidates.moderatorRequestsPage, setModeratorRequestsPage: candidates.setModeratorRequestsPage,
    moderatorInfoPage: candidates.moderatorInfoPage, setModeratorInfoPage: candidates.setModeratorInfoPage,
    contributorRequestsPage: candidates.contributorRequestsPage, setContributorRequestsPage: candidates.setContributorRequestsPage,
    contributorInfoPage: candidates.contributorInfoPage, setContributorInfoPage: candidates.setContributorInfoPage,
    moderatorsForPayout: candidates.moderatorsForPayout, usersForPayout: candidates.usersForPayout,
    moderatorsWithoutRequest: candidates.moderatorsWithoutRequest, paginatedModeratorInfo: candidates.paginatedModeratorInfo,
    contributorsWithoutRequest: candidates.contributorsWithoutRequest, paginatedContributorInfo: candidates.paginatedContributorInfo,
    moderatorRequestsToProcess: requests.moderatorRequestsToProcess, paginatedModeratorRequests: candidates.paginatedModeratorRequests,
    contributorRequestsToProcess: requests.contributorRequestsToProcess, paginatedContributorRequests: candidates.paginatedContributorRequests,
    myPayoutRequests: requests.myPayoutRequests, payoutRequests: requests.payoutRequests,
    moderatorRequestForm: moderatorEarnings.moderatorRequestForm, submittingModeratorRequest: moderatorEarnings.submittingModeratorRequest,
    payoutDialogOpen: payoutRequest.payoutDialogOpen, setPayoutDialogOpen: payoutRequest.setPayoutDialogOpen,
    profileForm: payoutRequest.profileForm, setProfileForm: payoutRequest.setProfileForm,
    submittingProfileForm: payoutRequest.submittingProfileForm,
    depositDialogOpen: admin.depositDialogOpen, setDepositDialogOpen: admin.setDepositDialogOpen,
    depositForm: admin.depositForm, setDepositForm: admin.setDepositForm, depositLoading: admin.depositLoading,
    bulkApproveDialog: requests.bulkApproveDialog, setBulkApproveDialog: requests.setBulkApproveDialog,
    rejectDialog: requests.rejectDialog, setRejectDialog: requests.setRejectDialog,
    hasPendingPayoutRequest: requests.hasPendingPayoutRequest, awaitingPayoutAmount: requests.awaitingPayoutAmount,
    formattedEarnings: formatCurrency(payoutState.earnedAmount, locale, currency),
    formattedRemainingAmount: formatCurrency(payoutState.remainingAmount, locale, currency),
    selectedSubmission: qualification.selectedSubmission, isStartTestBlocked: qualification.isStartTestBlocked,
    latestPendingUserContract: contracts.latestPendingUserContract, latestSignedUserContract: contracts.latestSignedUserContract,
    latestAwaitingFinalUserContract: contracts.latestAwaitingFinalUserContract,
    userContracts: contracts.userContracts, contractProgressByTest: contractsFlow.contractProgressByTest,
    getRequestPhone, renderPagination,

    loadUserContracts: contracts.loadUserContracts, loadPendingTests: moderation.loadPendingTests,
    loadCorrectedTests: moderation.loadCorrectedTests,
    loadValidatedTests: moderation.loadValidatedTests, loadModeratorContracts: contractsFlow.loadModeratorContracts,
    loadSubmissionDetails: correction.loadSubmissionDetails, loadMyPayoutRequests: requests.loadMyPayoutRequests,
    loadAdminPayoutRequests: requests.loadAdminPayoutRequests,
    loadEligibleUsers: candidates.loadEligibleUsers, loadEligibleModerators: candidates.loadEligibleModerators,
    handleModeratorNavChange: moderation.handleModeratorNavChange, handleBackFromDetails: correction.handleBackFromDetails,
    setScore: correction.setScore, handleSaveScores: correction.handleSaveScores, handleFinishCorrection: correction.handleFinishCorrection,
    handleSendContractForTest: contractsFlow.handleSendContractForTest, handleConfirmFinalValidate: contractsFlow.handleConfirmFinalValidate,
    handleDepositBudget: admin.handleDepositBudget, handleSubmitDeposit: admin.handleSubmitDeposit,
    handlePayUser: candidates.handlePayUser, handlePayModerator: candidates.handlePayModerator,
    handlePayAllModerators: candidates.handlePayAllModerators,
    handleApproveAdminRequest: requests.handleApproveAdminRequest,
    handleApproveAllModeratorRequests: requests.handleApproveAllModeratorRequests,
    handleApproveAllContributorRequests: requests.handleApproveAllContributorRequests,
    handleValidatePayoutRequest: requests.handleValidatePayoutRequest,
    handleSavePayoutSettings: admin.handleSavePayoutSettings, handleRequestPayout: payoutRequest.handleRequestPayout,
    handleViewContributions, handleOpenContracts,
    handleCompleteProfile: payoutRequest.handleCompleteProfile, handleContactSupport,
    handleProfileSubmit: payoutRequest.handleProfileSubmit, handleSubmitModeratorRequest: moderatorEarnings.handleSubmitModeratorRequest,
    executeBulkApprove: requests.executeBulkApprove, openRejectDialog: requests.openRejectDialog,
    handleRejectWithDialog: requests.handleRejectWithDialog,

    averages: correction.averages, totalWeighted: correction.totalWeighted, canValidate: correction.canValidate,
  };
}
